import { z } from "zod";
import { Decimal } from "decimal.js";
export const LIMITS = {
  fields: 100,
  queryable: 20,
  recordBytes: 65536,
  records: 100000,
  queryRows: 1000,
  httpBytes: 5 * 1024 * 1024,
  fileBytes: 20 * 1024 * 1024,
  expandedBytes: 100 * 1024 * 1024,
  pages: 1000,
  migrationRecords: 10000,
};
export const fieldSchema = z
  .object({
    id: z.uuid(),
    key: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/),
    label: z.string().trim().min(1).max(100),
    type: z.enum([
      "text",
      "integer",
      "decimal",
      "rating",
      "boolean",
      "date",
      "datetime",
      "enum",
      "json",
      "image",
    ]),
    nullable: z.boolean().default(true),
    queryable: z.boolean().default(false),
    hidden: z.boolean().default(false),
    active: z.boolean().default(true),
    inputHidden: z.boolean().optional(),
    defaultNow: z.boolean().optional(),
    stampNow: z.boolean().optional(),
    ratingStyle: z.enum(["number", "stars", "slider", "dots"]).optional(),
    calculation: z
      .object({
        op: z.literal("average"),
        fieldIds: z.array(z.uuid()).min(2).max(LIMITS.fields),
      })
      .strict()
      .optional(),
    identifier: z
      .object({
        mode: z.enum(["manual", "sequence", "uuid"]),
        prefix: z.string().max(40).default(""),
        suffix: z.string().max(40).default(""),
        digits: z.number().int().min(1).max(12).default(1),
      })
      .strict()
      .optional(),
    unit: z.string().max(30).optional(),
    scale: z.number().int().min(0).max(18).optional(),
    range: z
      .object({
        min: z.number().int().min(0).max(99),
        max: z.number().int().min(1).max(100),
      })
      .strict()
      .refine((r) => r.max > r.min && r.max - r.min <= 20, {
        message: "En skala skal have mellem 1 og 20 trin.",
      })
      .optional(),
    options: z.array(z.string().max(512)).max(100).optional(),
  })
  .strict();
export type Field = z.infer<typeof fieldSchema>;
export const ratingRange = (field: Field) => field.range ?? { min: 0, max: 5 };
export function calculationIssue(field: Field, fields: Field[]) {
  const calculation = field.calculation;
  if (!calculation) return;
  if (field.type !== "decimal" || !field.nullable)
    return "Et beregnet felt skal være et decimaltal og tillade tomme værdier.";
  if (!field.active) return;
  if (
    calculation.fieldIds.length < 2 ||
    new Set(calculation.fieldIds).size !== calculation.fieldIds.length
  )
    return "Vælg mindst to forskellige felter til gennemsnittet.";
  const sources = calculation.fieldIds.map((id) =>
    fields.find((f) => f.id === id),
  );
  if (
    sources.some(
      (f) =>
        !f ||
        !f.active ||
        f.id === field.id ||
        f.calculation ||
        !["integer", "decimal", "rating"].includes(f.type),
    )
  )
    return "Gennemsnittet skal bruge aktive tal- eller skalafelter uden beregninger.";
  const first = sources[0]!;
  if (
    sources.some((f) => (f!.unit ?? "").trim() !== (first.unit ?? "").trim()) ||
    (field.unit ?? "").trim() !== (first.unit ?? "").trim()
  )
    return "Felterne og gennemsnittet skal have samme enhed.";
  if (
    sources.some(
      (f) =>
        (f!.type === "rating") !== (first.type === "rating") ||
        (f!.type === "rating" &&
          (ratingRange(f!).min !== ratingRange(first).min ||
            ratingRange(f!).max !== ratingRange(first).max)),
    )
  )
    return "Skalafelter skal have samme minimum og maksimum og kan ikke blandes med almindelige tal.";
}
export const schemaDefinition = z
  .object({ fields: z.array(fieldSchema).min(1).max(LIMITS.fields) })
  .strict()
  .superRefine((s, c) => {
    for (const key of ["id", "key"] as const)
      if (new Set(s.fields.map((f) => f[key])).size !== s.fields.length)
        c.addIssue({ code: "custom", message: `Felt-${key} skal være unikt.` });
    if (
      s.fields.filter((f) => f.queryable && f.active).length > LIMITS.queryable
    )
      c.addIssue({
        code: "custom",
        message: "Højst 20 felter må bruges i forespørgsler.",
      });
    for (const f of s.fields) {
      const issue = calculationIssue(f, s.fields);
      if (issue)
        c.addIssue({ code: "custom", message: `${f.label}: ${issue}` });
      if (
        (f.defaultNow || f.stampNow) &&
        !["date", "datetime"].includes(f.type)
      )
        c.addIssue({
          code: "custom",
          message: "Nu kan kun bruges til dato og dato/tid.",
        });
      if (f.identifier && f.type !== "text")
        c.addIssue({
          code: "custom",
          message: "Et ID-felt skal gemmes som tekst.",
        });
      if (f.ratingStyle && f.type !== "rating")
        c.addIssue({
          code: "custom",
          message: "Skalavisning kræver et skalafelt.",
        });
      if (["json", "image"].includes(f.type) && f.queryable)
        c.addIssue({
          code: "custom",
          message: "JSON- og billedfelter kan ikke indekseres.",
        });
      if (f.type === "enum" && !f.options?.length)
        c.addIssue({
          code: "custom",
          message: "Et valgfelt skal have valgmuligheder.",
        });
    }
  });
export type Schema = z.infer<typeof schemaDefinition>;
export type Data = Record<string, unknown>;
export type ValidationIssue = { fieldId: string; message: string };
// Local precision keeps averages exact for the supported 38-digit inputs.
const CalculationDecimal = Decimal.clone({ precision: 80 });
export function calculateField(field: Field, data: Data): string | null {
  const values = field
    .calculation!.fieldIds.map((id) => data[id])
    .filter((value) => value !== null && value !== undefined && value !== "");
  if (!values.length) return null;
  const average = values
    .reduce<InstanceType<typeof CalculationDecimal>>(
      (sum, value) => sum.plus(String(value)),
      new CalculationDecimal(0),
    )
    .div(values.length);
  // Stay within the canonical decimal limits, including very large values.
  const integerDigits = Math.max(1, average.abs().trunc().toFixed().length);
  return average
    .toDecimalPlaces(
      Math.min(field.scale ?? 6, 38 - integerDigits),
      Decimal.ROUND_HALF_UP,
    )
    .toFixed();
}
export function stableJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return "[" + value.map(stableJson).join(",") + "]";
  return (
    "{" +
    Object.keys(value as Data)
      .sort()
      .map((k) => JSON.stringify(k) + ":" + stableJson((value as Data)[k]))
      .join(",") +
    "}"
  );
}
export function dateValid(v: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
  const d = new Date(v + "T00:00:00Z");
  return !Number.isNaN(d.valueOf()) && d.toISOString().slice(0, 10) === v;
}
export function canonicalValue(field: Field, value: unknown): unknown {
  if (value === null || value === undefined) {
    if (!field.nullable) throw new Error("Feltet skal udfyldes.");
    return null;
  }
  switch (field.type) {
    case "image":
      if (typeof value !== "string" || !z.uuid().safeParse(value).success)
        throw new Error("Upload et billede til feltet.");
      return value;
    case "text":
    case "enum":
      if (typeof value !== "string") throw new Error("Forventede tekst.");
      if (value.length > (field.queryable ? 512 : 16000))
        throw new Error("Teksten er for lang.");
      if (field.type === "enum" && !field.options?.includes(value))
        throw new Error("Vælg en tilladt værdi.");
      return value;
    case "integer":
    case "rating":
    case "decimal": {
      if (typeof value !== "string" || !/^[-+]?\d+(?:\.\d+)?$/.test(value))
        throw new Error("Angiv tal som tekst med punktum som decimaltegn.");
      const n = new Decimal(value);
      if (
        !n.isFinite() ||
        n.precision() > 38 ||
        n.abs().gte("1e38") ||
        n.decimalPlaces() > 18
      )
        throw new Error("Tallet overskrider 38 cifre eller 18 decimaler.");
      if (["integer", "rating"].includes(field.type) && !n.isInteger())
        throw new Error("Forventede et heltal.");
      if (field.type === "rating") {
        const { min, max } = ratingRange(field);
        if (n.lt(min) || n.gt(max))
          throw new Error(`Vælg et heltal mellem ${min} og ${max}.`);
      }
      if (field.scale !== undefined && n.decimalPlaces() > field.scale)
        throw new Error(`Højst ${field.scale} decimaler.`);
      return n.toFixed();
    }
    case "boolean":
      if (typeof value !== "boolean") throw new Error("Forventede sand/falsk.");
      return value;
    case "date":
      if (typeof value !== "string" || !dateValid(value))
        throw new Error("Brug en gyldig dato: YYYY-MM-DD.");
      return value;
    case "datetime":
      if (
        typeof value !== "string" ||
        !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(
          value,
        ) ||
        !dateValid(value.slice(0, 10)) ||
        Number.isNaN(Date.parse(value))
      )
        throw new Error("Dato og tid skal inkludere tidszone.");
      return new Date(value).toISOString();
    case "json":
      return JSON.parse(JSON.stringify(value));
  }
}
export function validateRecord(schema: Schema, input: Data) {
  const data: Data = {},
    issues: ValidationIssue[] = [];
  const ids = new Set(schema.fields.filter((f) => f.active).map((f) => f.id));
  for (const id of Object.keys(input))
    if (!ids.has(id))
      issues.push({ fieldId: id, message: "Ukendt eller deaktiveret felt." });
  for (const f of schema.fields.filter((f) => f.active && !f.calculation)) {
    try {
      data[f.id] = canonicalValue(f, input[f.id]);
    } catch (e) {
      issues.push({ fieldId: f.id, message: (e as Error).message });
    }
  }
  for (const f of schema.fields.filter((f) => f.active && f.calculation)) {
    try {
      // Supplied/stored calculated values are always replaced by the server's result.
      data[f.id] = canonicalValue(f, calculateField(f, data));
    } catch (e) {
      issues.push({ fieldId: f.id, message: (e as Error).message });
    }
  }
  if (
    new TextEncoder().encode(stableJson(data)).byteLength > LIMITS.recordBytes
  )
    issues.push({ fieldId: "", message: "Rækken må højst være 64 KiB." });
  return { data, issues };
}
export const pathSchema = z
  .string()
  .max(512)
  .refine(
    (p) =>
      p === "" ||
      p
        .split(".")
        .every(
          (s) =>
            /^[\w-]+$/.test(s) &&
            !["__proto__", "prototype", "constructor"].includes(s),
        ),
    "Ugyldig JSON-sti.",
  );
export function getPath(value: unknown, path: string): unknown {
  if (path === "") return value;
  let cur = value;
  for (const part of path.split(".")) {
    if (
      !cur ||
      typeof cur !== "object" ||
      !Object.hasOwn(cur, part) ||
      ["__proto__", "prototype", "constructor"].includes(part)
    )
      return undefined;
    cur = (cur as Data)[part];
  }
  return cur;
}
export const mappingSchema = z
  .object({
    fields: z
      .array(
        z.object({
          fieldId: z.uuid(),
          path: pathSchema,
          transform: z
            .enum([
              "none",
              "string",
              "number",
              "boolean",
              "date",
              "datetime",
              "json",
            ])
            .default("none"),
          dateFormat: z
            .enum(["iso", "dd/MM/yyyy", "MM/dd/yyyy"])
            .default("iso"),
        }),
      )
      .max(100),
    identity: z
      .object({
        mode: z.enum(["external", "fingerprint"]),
        paths: z.array(pathSchema).min(1).max(10),
      })
      .strict(),
    ignoredPaths: z.array(pathSchema).max(100).default([]),
    updatedAtPath: pathSchema.optional(),
    occurredAtPath: pathSchema.optional(),
  })
  .strict();
export type Mapping = z.infer<typeof mappingSchema>;
export function mapRow(row: unknown, mapping: Mapping): Data {
  const result: Data = {};
  for (const m of mapping.fields) {
    let v = getPath(row, m.path);
    if (v === undefined || v === null || v === "") {
      result[m.fieldId] = null;
      continue;
    }
    switch (m.transform) {
      case "string":
        if (typeof v === "object")
          throw new Error("Et objekt kan ikke blive et tekstfelt.");
        v = String(v);
        break;
      case "number":
        if (
          typeof v === "number" &&
          !Number.isSafeInteger(v) &&
          Number.isInteger(v)
        )
          throw new Error(
            "Kilden har et usikkert numerisk ID; send værdien som tekst.",
          );
        v = String(v);
        break;
      case "boolean":
        if (v === "true" || v === 1 || v === "1") v = true;
        else if (v === "false" || v === 0 || v === "0") v = false;
        break;
      case "date":
        if (m.dateFormat !== "iso" && typeof v === "string") {
          const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
          if (!match) throw new Error("Datoen matcher ikke valgt format.");
          v =
            m.dateFormat === "dd/MM/yyyy"
              ? `${match[3]}-${match[2]}-${match[1]}`
              : `${match[3]}-${match[1]}-${match[2]}`;
        }
        break;
      case "datetime":
        if (typeof v === "number") v = new Date(v).toISOString();
        break;
      case "json":
        if (typeof v === "string") v = JSON.parse(v);
        break;
    }
    result[m.fieldId] = v;
  }
  return result;
}
export const filterSchema = z
  .object({
    fieldId: z.uuid(),
    op: z.enum([
      "eq",
      "ne",
      "gt",
      "gte",
      "lt",
      "lte",
      "contains",
      "in",
      "is_null",
      "is_not_null",
    ]),
    value: z
      .union([
        z.string().max(512),
        z.boolean(),
        z.array(z.string().max(512)).max(100),
      ])
      .optional(),
  })
  .strict();
export const querySchema = z
  .object({
    filters: z.array(filterSchema).max(20).default([]),
    filterMode: z.enum(["and", "or"]).default("and"),
    period: z
      .object({
        fieldId: z.uuid(),
        from: z.iso.datetime({ offset: true }),
        to: z.iso.datetime({ offset: true }),
        timezone: z.string().max(80).default("UTC"),
      })
      .strict()
      .optional(),
    dimensions: z
      .array(
        z.object({
          fieldId: z.uuid(),
          bucket: z.enum(["day", "week", "month"]).optional(),
        }),
      )
      .max(2)
      .default([]),
    metrics: z
      .array(
        z
          .object({
            op: z.enum([
              "count_records",
              "count_values",
              "sum",
              "avg",
              "min",
              "max",
            ]),
            fieldId: z.uuid().optional(),
            alias: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/),
          })
          .strict(),
      )
      .max(10)
      .default([]),
    sort: z
      .object({
        fieldId: z.uuid().optional(),
        metricAlias: z
          .string()
          .regex(/^[a-zA-Z][a-zA-Z0-9_]{0,40}$/)
          .optional(),
        direction: z.enum(["asc", "desc"]).default("desc"),
      })
      .optional(),
    limit: z.number().int().min(1).max(1000).default(100),
    groupPage: z.boolean().optional(),
    cursor: z.string().max(4096).optional(),
  })
  .strict();
export type QuerySpec = z.infer<typeof querySchema>;
export const httpConfigSchema = z
  .object({
    url: z.url().max(2048),
    method: z.enum(["GET", "POST"]).default("GET"),
    readOnlyPost: z.boolean().default(false),
    body: z.unknown().optional(),
    itemsPath: pathSchema.default(""),
    pagination: z
      .object({
        mode: z.enum(["none", "page", "cursor"]).default("none"),
        parameter: z
          .string()
          .regex(/^[\w-]{1,60}$/)
          .default("page"),
        start: z.number().int().min(0).default(1),
        nextPath: pathSchema.optional(),
      })
      .default({ mode: "none", parameter: "page", start: 1 }),
    incremental: z
      .object({
        parameter: z.string().regex(/^[\w-]{1,60}$/),
        path: pathSchema,
        overlapSeconds: z.number().int().min(0).max(86400).default(300),
      })
      .optional(),
  })
  .strict()
  .refine((c) => c.method !== "POST" || c.readOnlyPost, {
    message: "POST skal bekræftes som en læseoperation.",
  });
export type HttpConfig = z.infer<typeof httpConfigSchema>;
export const datasetIconNames = [
  "table",
  "activity",
  "finance",
  "health",
  "work",
  "food",
  "chart",
  "folder",
] as const;
export const datasetAppearanceSchema = z.object({
  websiteUrl: z.url().max(2048).nullable().optional(),
  fetchFavicon: z.boolean().optional(),
  iconName: z.enum(datasetIconNames).nullable().optional(),
  iconUrl: z.url().max(2048).nullable().optional(),
});
export const periodWindowSchema = z
  .object({
    fieldId: z.uuid(),
    unit: z.enum(["day", "week", "month"]),
    count: z.number().int().min(1).max(366),
    timezone: z.string().max(80),
  })
  .strict();
export const viewSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    kind: z.enum([
      "table",
      "line",
      "bar",
      "kpi",
      "form",
      "ranking",
      "calendar",
    ]),
    dashboardId: z.uuid().optional(),
    query: querySchema,
    config: z
      .object({
        refreshSeconds: z.number().int().min(30).max(86400).optional(),
        fieldIds: z.array(z.uuid()).max(100).optional(),
        scaleBars: z.array(z.uuid()).max(100).optional(),
        showUpdatedAt: z.boolean().optional(),
        title: z.string().max(160).optional(),
        onDashboard: z.boolean().optional(),
        rankingCount: z.union([z.literal(3), z.literal(5)]).optional(),
        calendarYear: z.number().int().min(1900).max(2200).optional(),
        calendarTimezone: z.string().max(80).optional(),
        window: periodWindowSchema.optional(),
      })
      .strict()
      .default({}),
  })
  .strict();
export type ViewInput = z.infer<typeof viewSchema>;
export type Capability =
  | "read"
  | "write"
  | "schema"
  | "sources"
  | "automate"
  | "export"
  | "create"
  | "edit_own";
export function formatValue(value: unknown, field?: Field) {
  if (value === null || value === undefined) return "—";
  if (field?.type === "image") return "Billede";
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "boolean") return value ? "Ja" : "Nej";
  if (field?.type === "datetime")
    return new Date(String(value)).toLocaleString("da-DK");
  if (field?.type === "date") {
    const date = String(value).slice(0, 10).split("-");
    return date.length === 3 ? date.reverse().join(".") : String(value);
  }
  if (field && ["integer", "decimal", "rating"].includes(field.type))
    return formatDecimal(value);
  return String(value);
}
export function formatDecimal(value: unknown) {
  if (value === null || value === undefined) return "—";
  const text = String(value),
    match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) return text;
  const fraction = match[3]?.replace(/0+$/, "");
  return (
    match[1] +
    match[2]!.replace(/\B(?=(\d{3})+(?!\d))/g, ".") +
    (fraction ? "," + fraction : "")
  );
}
